<template>
  <generic-container
    :title="$t('report composer')"
    icon="line chart"
    :header_buttons="header_buttons"
  >
    <div class="ui container">
      <div class="ui five item pointing menu">
        <a data-tab="general" class="item active">
          <i class="info icon"></i>
          {{ $t("general") }}
        </a>
        <a data-tab="input_screen" class="item">
          <i class="keyboard icon"></i>
          {{ $t("input screen") }}
        </a>
        <a data-tab="result_screen" class="item">
          <i class="poll icon"></i>
          {{ $t("result screen") }}
        </a>
        <a data-tab="datasource" class="item">
          <i class="database icon"></i>
          {{ $t("datasource") }}
        </a>
        <a data-tab="permissions" class="item">
          <i class="key icon"></i>
          {{ $t("permissions") }}
        </a>
      </div>
      <div data-tab="general" class="ui bottom attached tab active">
        <form class="ui form">
          <div class="two fields">
            <div class="field">
              <label>{{ $t("name") }}</label>
              <input
                type="text"
                name="name"
                v-model="name"
                ref="name_field"
                :placeholder="$t('name')"
              />
            </div>
            <div class="field">
              <label>{{ $t("project") }}</label>
              <div class="ui search selection dropdown" ref="project_dropdown">
                <input type="hidden" name="project" />
                <i class="dropdown icon"></i>
                <div class="default text">{{ $t("project") }}</div>
                <div class="menu"></div>
              </div>
            </div>
          </div>
          <div class="field">
            <label>{{ $t("description") }}</label>
            <textarea name="description" v-model="description" rows="2"></textarea>
          </div>
        </form>
      </div>
      <div data-tab="input_screen" class="ui bottom attached tab">
        <div class="ui tabular menu">
          <a data-tab="input_screen_html" class="item active">
            <i class="html5 icon"></i>
            {{ $t("html") }}
          </a>
          <a data-tab="input_screen_js" class="item">
            <i class="js icon"></i>
            {{ $t("javascript") }}
          </a>
        </div>
        <div data-tab="input_screen_html" class="ui bottom attached tab active">
          <html-templates
            @insert_template="insert_input_template"
            :templates="templates"
            @format="format_input_html"
          />
          <editor
            @init="init_code('html')"
            @input="update_input_preview"
            v-model="setup.input_screen_html"
            lang="html"
            theme="monokai"
            ref="html_input_editor"
          ></editor>
          <div class="ui dividing header" v-if="setup.input_screen_html">{{ $t("preview") }}</div>
          <div ref="input_preview"></div>
          <div class="ui form">
            <div class="three fields" style="margin-top:10px">
              <div class="field">
                <label>Fecha inicial</label>
                <div class="ui calendar">
                  <div class="ui input left icon">
                    <i class="calendar icon"></i>
                    <input type="text" placeholder="Fecha inicial" />
                  </div>
                </div>
              </div>
              <div class="field">
                <label>Fecha final</label>
                <div class="ui calendar">
                  <div class="ui input left icon">
                    <i class="calendar icon"></i>
                    <input type="text" placeholder="Fecha final" />
                  </div>
                </div>
              </div>
              <div class="field">
                <label for>{{ $t("query") }}</label>
                <div class="ui blue icon fluid labeled button">
                  <i class="check icon"></i>
                  {{ $t("get data")}}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div data-tab="input_screen_js" class="ui bottom attached tab">
          <div class="ui info message">
            <div class="header">{{ $t("hints") }}</div>
            <ul class="list">
              <li v-html="$t('use mixin for mixin name')"></li>
              <li>
                <div
                  class="ui mini icon labeled blue button hintbtn"
                  :data-html="'<pre>' + this.default_mixin + '</pre>'"
                >
                  <i class="eye icon"></i>
                  {{ $t("basic structure example") }}
                </div>
              </li>
            </ul>
          </div>
          <editor
            @init="init_code('javascript')"
            v-model="setup.input_screen_js"
            lang="javascript"
            theme="monokai"
          ></editor>
        </div>
      </div>
      <div data-tab="result_screen" class="ui bottom attached tab">
        <div class="ui tabular menu">
          <a data-tab="result_screen_html" class="item active">
            <i class="html5 icon"></i>
            {{ $t("html") }}
          </a>
          <a data-tab="result_screen_js" class="item">
            <i class="js icon"></i>
            {{ $t("javascript") }}
          </a>
        </div>
        <div data-tab="result_screen_html" class="ui bottom attached tab active">
          <html-templates
            @insert_template="insert_result_template"
            :templates="templates"
            @format="format_result_html"
          />
          <editor
            @init="init_code('html')"
            @input="update_result_preview"
            v-model="setup.result_screen_html"
            lang="html"
            theme="monokai"
            ref="html_result_editor"
          ></editor>
          <div class="ui dividing header" v-if="setup.result_screen_html">{{ $t("preview") }}</div>
          <div ref="result_preview"></div>
        </div>
        <div data-tab="result_screen_js" class="ui bottom attached tab">
          <div class="ui info message">
            <div class="header">{{ $t("hints") }}</div>
            <ul class="list">
              <li v-html="$t('use mixin1 for mixin name')"></li>
              <li v-html="$t('result data is stored in result variable')"></li>
              <li
                v-html="$t('access to each query result referencing it by name in the result variable (result.query_name)')"
              ></li>
              <li>
                <div
                  class="ui mini icon labeled blue button hintbtn"
                  ref="tooltip1"
                  :data-html="'<pre>' + this.default_mixin1 + '</pre>'"
                >
                  <i class="eye icon"></i>
                  {{ $t("basic structure example") }}
                </div>
              </li>
            </ul>
          </div>
          <editor
            @init="init_code('javascript')"
            v-model="setup.result_screen_js"
            lang="javascript"
            theme="monokai"
          ></editor>
        </div>
      </div>
      <div data-tab="datasource" class="ui bottom attached tab">
        <datasource-configuration
          :queries="setup.queries"
          :datasources="datasources"
          ref="datasource_configuration"
        />
        <!-- <generic-container title>
          <div class="ui form">
            <h3 class="ui dividing header">{{ $t("configure datasource") }}</h3>
            <div class="five column wide field">
              <label for>{{ $t("datasource") }}</label>
              <div class="ui selection dropdown" ref="datasource_dropdown">
                <input type="hidden" name="datasource" />
                <i class="dropdown icon"></i>
                <div class="default text"></div>
                <div class="menu"></div>
              </div>
            </div>
            <div id="rqb"></div>
            <div class="field">
              <editor
                @init="init_code('sql')"
                v-model="setup.database_query"
                lang="sql"
                theme="monokai"
              ></editor>
            </div>
          </div>
        </generic-container>-->
      </div>
      <div data-tab="permissions" class="ui bottom attached tab">
        <table class="ui compact celled definition table">
          <thead>
            <tr>
              <th></th>
              <th>{{ $t("role") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(role, index) in roles" :key="index">
              <td class="collapsing">
                <div class="ui fitted slider checkbox">
                  <input type="checkbox" v-model="roles[index].granted" />
                  <label></label>
                </div>
              </td>
              <td>{{ role.name }}</td>
            </tr>
          </tbody>
          <tfoot class="full-width">
            <tr>
              <th></th>
              <th colspan="4">
                <div
                  class="ui right floated small primary labeled icon button"
                  @click="apply_permissions"
                >
                  <i class="check icon"></i>
                  {{ $t("apply") }}
                </div>
              </th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </generic-container>
</template>

<script>
import { local_api, stringify } from "@/utils";
import filters from "@/filters";
import Editor from "vue2-ace-editor";
import GenericContainer from "@/components/GenericContainer";
import HtmlTemplates from "../HtmlTemplates";
import { html } from "js-beautify";
import DatasourceConfiguration from "./DatasourceConfiguration";

export default {
  name: "NgineDashboardComposer",
  components: {
    editor: Editor,
    GenericContainer,
    HtmlTemplates,
    DatasourceConfiguration
  },
  filters: filters,
  props: {
    templates: Array,
    projects: Array
  },
  data() {
    return {
      uuid: "",
      name: "",
      description: "",
      project: "",
      setup: {
        input_screen_html: "",
        input_screen_js: "", //this.default_mixin,
        result_screen_html: "",
        result_screen_js: "", // this.default_mixin1,
        queries: []
      },
      roles: [],
      header_buttons: [
        { icon: "blue save", tooltip: this.$t("save"), click: this.save },
        { icon: "eraser", tooltip: this.$t("clear"), click: this.create_report }
      ],
      datasources: []
    };
  },
  computed: {
    default_mixin: function() {
      return `mixin = {
    data: function() {
        return {
        }
    },
    methods: {
    },
    created: function() {
    },
    mounted: function() {
    }
}`;
    },
    default_mixin1: function() {
      return `mixin1 = {
    data: function() {
        return {
        }
    },
    methods: {
    },
    created: function() {
    },
    mounted: function() {
    }
}`;
    }
  },
  methods: {
    init_code(type) {
      require(`brace/mode/${type}`);
      require("brace/theme/monokai");
    },
    update_input_preview(a) {
      let vm = this;
      window.jQuery(vm.$refs.input_preview).html(a);
    },
    update_result_preview(a) {
      let vm = this;
      window.jQuery(vm.$refs.result_preview).html(a);
    },
    load_report(report) {
      let vm = this;

      vm.roles.map(r => {
        r.granted = false;
      });

      local_api(vm, "post", "/ngine_dashboard/composer/do-get-report", {
        i: report.uuid
      })
        .then(r => {
          vm.uuid = report.uuid;
          vm.name = r.name;
          r.setup = JSON.parse(r.setup);
          vm.description = r.description;
          vm.setup.input_screen_html = r.setup.input_screen_html || "";
          vm.setup.input_screen_js =
            r.setup.input_screen_js || vm.default_mixin;
          vm.setup.result_screen_html = r.setup.result_screen_html || "";
          vm.setup.result_screen_js =
            r.setup.result_screen_js || vm.default_mixin;
          vm.setup.queries = r.setup.queries || [];
          if (r.project) {
            vm.project = r.project;
            window
              .jQuery(vm.$refs.project_dropdown)
              .dropdown("set selected", r.project);
          }

          r.ngn_report_suscriptions.map(s => {
            let trgt = vm.roles.find(r => r.role_id == s.role_id);
            if (trgt) trgt.granted = true;
          });
        })
        .catch(err => {
          vm.$alertify.error(stringify(err));
        });
    },
    get_report_data() {
      return {
        uuid: this.uuid,
        name: this.name,
        project: this.project,
        description: this.description,
        setup: this.setup
      };
    },
    apply_permissions() {
      let vm = this;
      let granted = vm.roles
        .filter(r => r.granted)
        .map(r => {
          return r.role_id;
        });
      if (vm.uuid) {
        local_api(
          vm,
          "post",
          "/ngine_dashboard/composer/do-apply-suscriptions",
          {
            i: vm.uuid,
            r: granted
          }
        )
          .then(() => {
            vm.$alertify.success(vm.$t("suscriptions configured"));
          })
          .catch(err => {
            vm.$alertify.error(stringify(err));
          });
      } else this.$alertify.warning(this.$t("no report selected yet"));
    },
    save() {
      let vm = this;
      let data = vm.get_report_data();
      local_api(vm, "post", "/ngine_dashboard/composer/do-save", data)
        .then(() => {
          vm.$alertify.success(vm.$t("report saved"));
        })
        .catch(err => {
          vm.$alertify.error(stringify(err));
        });
    },
    insert_input_template(template) {
      this.$refs.html_input_editor.editor.session.insert(
        this.$refs.html_input_editor.editor.getCursorPosition(),
        template + "\n"
      );

      let ress = html(this.setup.input_screen_html);
      this.setup.input_screen_html = ress;
    },
    insert_result_template(template) {
      this.$refs.html_result_editor.editor.session.insert(
        this.$refs.html_result_editor.editor.getCursorPosition(),
        template + "\n"
      );

      let ress = html(this.setup.result_screen_html);
      this.setup.result_screen_html = ress;
    },
    format_input_html() {
      let ress = html(this.setup.input_screen_html);
      this.setup.input_screen_html = ress;
    },
    format_result_html() {
      let ress = html(this.setup.result_screen_html);
      this.setup.result_screen_html = ress;
    },
    create_report() {
      let vm = this;

      vm.uuid = "";
      vm.name = "";
      vm.description = "";
      vm.project = "";
      vm.setup = {
        input_screen_html: "",
        input_screen_js: vm.default_mixin,
        result_screen_html: "",
        result_screen_js: vm.default_mixin1,
        queries: []
      };

      vm.$refs.name_field.focus();
    }
  },
  created() {
    let vm = this;

    // vm.$loadScript("/assets/")
    //   .then(() => {
    //     console.log("redqueryloaded");
    //     // eslint-disable-next-line no-undef
    //     console.log(RedQueryBuilderFactory);
    //   })
    //   .catch((err) => {
    //     console.error(err);
    //   });

    // var script = document.createElement("script");
    // script.setAttribute("type", "text/javascript");
    // script.setAttribute(
    //   "src",
    //   "/images/assets/RedQueryBuilder/RedQueryBuilderFactory.nocache"
    // );
    // document.getElementsByTagName("head")[0].appendChild(script);

    local_api(vm, "post", "/user_role/do-get-all")
      .then(_roles => {
        vm.roles = _roles.map(r => {
          r.granted = false;
          return r;
        });
      })
      .catch(err => {
        console.error(err);
      });

    local_api(vm, "post", "/ngine_core/datasource/do-get-list")
      .then(datasources => {
        vm.datasources = datasources;
      })
      .catch(err => {
        console.error(err);
      });
  },
  mounted() {
    let vm = this;
    window.jQuery(".ui.pointing.menu .item").tab();
    window.jQuery(".ui.tabular.menu .item").tab();

    window.jQuery(vm.$refs.project_dropdown).dropdown({
      allowAdditions: true,
      onChange(value) {
        vm.project = value;
      }
    });
    // .dropdown("setup menu", { values: vls });

    window.jQuery(".hintbtn").popup({
      on: "click"
    });

    vm.setup.input_screen_js = vm.default_mixin;
    vm.setup.result_screen_js = vm.default_mixin1;
  },
  watch: {
    projects() {
      let vm = this;
      let vls = vm.projects.map(p => {
        return { name: p, value: p };
      });
      window
        .jQuery(vm.$refs.project_dropdown)
        .dropdown({
          allowAdditions: true,
          onChange(value) {
            vm.project = value;
          }
        })
        .dropdown("setup menu", { values: vls });
    }
  }
};
</script>
